import { motion } from 'framer-motion';
import { MaterialIcon } from '@/components/MaterialIcon';
import { StatusBadge } from '@/components/StatusBadge';
import { RUNTIME_ENV_VARS } from '@/constants/env-vars';
import { cn } from '@/utils/classnames';

/**
 * Screen 7 — visual structure mirrors docs/frontend/runtime_config_cbwee_identity_fabric/code.html.
 *
 * Strict boundary rules from design.md:
 *   - The console NEVER reads real env-var values. Status is a fixed front-end
 *     label ("运维侧维护，控制台不读取" or "MASKED") — there is no
 *     configured/missing probe.
 *   - Compose service status is a static documentation card, not a live probe.
 *   - Bootstrap admin allowlist is described in prose only.
 */
export function RuntimeConfigPage() {
  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-panel-gap">
      <Header />

      <div className="grid grid-cols-1 items-start gap-panel-gap lg:grid-cols-12">
        <div className="flex flex-col gap-panel-gap lg:col-span-8">
          <EnvVarsCard />
        </div>
        <div className="flex flex-col gap-panel-gap lg:col-span-4">
          <ComposeServicesCard />
          <BootstrapAdminCard />
        </div>
      </div>
    </div>
  );
}

const cardStagger = (index: number) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.22, delay: index * 0.05, ease: 'easeOut' as const },
});

function Header() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className="mb-stack-sm flex flex-col gap-unit"
    >
      <h2 className="font-headline-lg text-headline-lg text-on-background">Runtime Config</h2>
      <p className="font-body-md text-body-md text-on-surface-variant">
        环境变量与运行时由运维侧维护，控制台只列举键名，不读取真实值。
      </p>
    </motion.div>
  );
}

function EnvVarsCard() {
  return (
    <motion.section
      {...cardStagger(0)}
      className="overflow-hidden rounded border border-[#DFE1E6] bg-surface"
    >
      <header className="flex items-center justify-between border-b border-[#DFE1E6] bg-[#EBECF0] px-container-padding py-3">
        <h3 className="flex items-center gap-stack-sm font-label-caps text-label-caps uppercase text-on-surface">
          <MaterialIcon name="data_object" size={16} />
          Environment Variables
        </h3>
        <span className="font-label-caps text-label-caps uppercase text-on-surface-variant">
          {RUNTIME_ENV_VARS.length} keys · 仅展示键名
        </span>
      </header>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-[#DFE1E6]">
              <th className="p-table-cell-padding font-label-caps text-label-caps uppercase text-on-surface-variant">
                Variable Name
              </th>
              <th className="p-table-cell-padding font-label-caps text-label-caps uppercase text-on-surface-variant">
                Description
              </th>
              <th className="p-table-cell-padding font-label-caps text-label-caps uppercase text-on-surface-variant w-44">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#DFE1E6] font-code-md text-code-md">
            {RUNTIME_ENV_VARS.map((spec) => {
              const isMasked = spec.displayState === 'masked';
              return (
                <tr key={spec.name} className="transition-colors hover:bg-surface-container-low">
                  <td className="p-table-cell-padding text-on-surface align-top">
                    <div className="flex items-center gap-stack-sm">
                      <span className="break-all">{spec.name}</span>
                      {spec.sensitive ? (
                        <span title="敏感字段" className="text-outline">
                          <MaterialIcon name="lock" size={14} />
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="p-table-cell-padding align-top font-body-sm text-body-sm text-on-surface-variant">
                    {spec.description}
                  </td>
                  <td className="p-table-cell-padding align-top">
                    {isMasked ? (
                      <StatusBadge variant="warning" dot>
                        MASKED
                      </StatusBadge>
                    ) : (
                      <StatusBadge variant="neutral" dot>
                        运维侧维护
                      </StatusBadge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </motion.section>
  );
}

function ComposeServicesCard() {
  return (
    <motion.section
      {...cardStagger(1)}
      className="overflow-hidden rounded border border-[#DFE1E6] bg-surface"
    >
      <header className="border-b border-[#DFE1E6] bg-[#EBECF0] px-container-padding py-3">
        <h3 className="flex items-center gap-stack-sm font-label-caps text-label-caps uppercase text-on-surface">
          <MaterialIcon name="dns" size={16} />
          Docker Compose 服务
        </h3>
      </header>
      <div className="flex flex-col gap-stack-md p-container-padding">
        <ServiceCard
          name="identity-fabric"
          subtitle="端口 8080 · healthcheck → GET /healthz"
          tag="Port 8080"
        />
        <ServiceCard
          name="postgres"
          subtitle="postgres:16-alpine · pg_isready 健康检查"
          tag="Port 5432"
        />
        <div className="rounded border border-[#DFE1E6] bg-surface-container-lowest p-3">
          <div className="flex items-start gap-stack-sm">
            <span className="text-primary">
              <MaterialIcon name="play_circle" size={18} filled />
            </span>
            <div>
              <div className="font-code-md text-code-md text-on-surface">
                sqlx::migrate!("./migrations")
              </div>
              <div className="mt-unit font-body-sm text-body-sm text-on-surface-variant">
                程序启动自动跑迁移，无需手工 SQL。
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}

function ServiceCard({
  name,
  subtitle,
  tag,
}: {
  name: string;
  subtitle: string;
  tag: string;
}) {
  return (
    <div className="flex items-center justify-between rounded border border-[#DFE1E6] bg-surface-container-lowest p-3">
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded border border-[#CEEAD6] bg-[#E6F4EA]">
          <MaterialIcon name="check_circle" size={18} className="text-[#137333]" filled />
        </div>
        <div>
          <div className="font-code-md text-code-md font-semibold text-on-surface">{name}</div>
          <div className="font-body-sm text-body-sm text-on-surface-variant">{subtitle}</div>
        </div>
      </div>
      <span className="rounded border border-outline-variant bg-surface-container-low px-2 py-0.5 font-body-sm text-body-sm text-on-surface-variant">
        {tag}
      </span>
    </div>
  );
}

function BootstrapAdminCard() {
  return (
    <motion.section
      {...cardStagger(2)}
      className={cn('relative overflow-hidden rounded border border-primary-fixed-dim bg-surface-container-low')}
    >
      <div className="absolute left-0 top-0 h-full w-1 bg-primary-container" aria-hidden />
      <div className="p-container-padding">
        <h3 className="mb-stack-sm flex items-center gap-stack-sm font-headline-md text-headline-md text-on-surface">
          <span className="text-primary-container">
            <MaterialIcon name="admin_panel_settings" size={20} filled />
          </span>
          Bootstrap Admin Provisioning
        </h3>
        <p className="mb-stack-md font-body-sm text-body-sm leading-relaxed text-on-surface-variant">
          首次登录的飞书用户若 mobile 命中{' '}
          <code className="rounded border border-outline-variant bg-surface-container-high px-1 font-code-md text-[11px] text-on-surface">
            CBWEE_IDENTITY__BOOTSTRAP_ADMIN_MOBILES
          </code>
          ，或 union_id 命中{' '}
          <code className="rounded border border-outline-variant bg-surface-container-high px-1 font-code-md text-[11px] text-on-surface">
            CBWEE_IDENTITY__BOOTSTRAP_ADMIN_UNION_IDS
          </code>
          ，会被授予 <code className="font-code-md text-[11px] text-on-surface">__platform__</code>{' '}
          应用下的 <code className="font-code-md text-[11px] text-on-surface">platform_admin</code>{' '}
          角色。后续登录不再 bootstrap。
        </p>
        <div className="rounded border border-[#DFE1E6] bg-surface p-3 text-sm">
          <div className="mb-stack-sm border-b border-[#DFE1E6] pb-1 font-label-caps text-label-caps uppercase text-on-surface-variant">
            匹配条件
          </div>
          <ul className="space-y-stack-sm font-body-sm text-body-sm text-on-surface">
            <li className="flex items-start gap-stack-sm">
              <span className="mt-0.5 text-outline">
                <MaterialIcon name="phone_iphone" size={14} />
              </span>
              <span>
                <strong>Mobile：</strong>飞书 user_info 返回的 mobile 字段，与逗号分隔白名单做精确匹配。
              </span>
            </li>
            <li className="flex items-start gap-stack-sm">
              <span className="mt-0.5 text-outline">
                <MaterialIcon name="badge" size={14} />
              </span>
              <span>
                <strong>Union ID：</strong>飞书 user_info 返回的 union_id 字段，与逗号分隔白名单做精确匹配。
              </span>
            </li>
          </ul>
        </div>
        <div className="mt-stack-md flex items-start gap-stack-sm rounded bg-surface-container-high p-stack-sm">
          <span className="mt-0.5 text-on-surface-variant">
            <MaterialIcon name="info" size={16} />
          </span>
          <p className="m-0 font-body-sm text-body-sm text-on-surface-variant">
            控制台不读取也不展示真实白名单内容；如需修改请在运维侧更新环境变量并重启 identity-fabric 容器（
            <code className="font-code-md text-[11px] text-on-surface">docker compose restart</code>{' '}
            不会重新加载 .env）。
          </p>
        </div>
      </div>
    </motion.section>
  );
}
